/**
 * Smart Widget Configuration
 * Defines states, dimensions, and breakpoints for the chat widget
 */

// Widget States
export const WIDGET_STATES = {
  COLLAPSED: 'collapsed',
  HALF: 'half',
  FULL: 'full',
};

// Device Types
export const DEVICE_TYPES = {
  MOBILE: 'mobile',
  TABLET: 'tablet',
  DESKTOP: 'desktop',
};

// Breakpoints
export const BREAKPOINTS = {
  MOBILE: 480,
  TABLET: 768,
  DESKTOP: 1024,
};

// Widget Dimensions per state and device
export const WIDGET_DIMENSIONS = {
  [DEVICE_TYPES.MOBILE]: {
    [WIDGET_STATES.COLLAPSED]: { width: '70px', height: '70px' },
    [WIDGET_STATES.HALF]: { width: '100vw', height: '50dvh' },
    [WIDGET_STATES.FULL]: { width: '100vw', height: '100dvh' },
  },
  [DEVICE_TYPES.TABLET]: {
    [WIDGET_STATES.COLLAPSED]: { width: '80px', height: '80px' },
    [WIDGET_STATES.HALF]: { width: '380px', height: '50vh' },
    [WIDGET_STATES.FULL]: { width: '100vw', height: '85vh' },
  },
  [DEVICE_TYPES.DESKTOP]: {
    [WIDGET_STATES.COLLAPSED]: { width: '350px', height: '160px' },
    [WIDGET_STATES.HALF]: { width: '400px', height: '50vh' },
    [WIDGET_STATES.FULL]: { width: '400px', height: '70vh' },
  },
};

// Fallback dimensions (for older browsers without dvh support)
export const WIDGET_DIMENSIONS_FALLBACK = {
  [DEVICE_TYPES.MOBILE]: {
    [WIDGET_STATES.HALF]: { width: '100vw', height: '50vh' },
    [WIDGET_STATES.FULL]: { width: '100vw', height: '100vh' },
  },
  [DEVICE_TYPES.TABLET]: {
    [WIDGET_STATES.HALF]: { width: '380px', height: '50vh' },
    [WIDGET_STATES.FULL]: { width: '100vw', height: '85vh' },
  },
  [DEVICE_TYPES.DESKTOP]: {
    [WIDGET_STATES.HALF]: { width: '400px', height: '50vh' },
    [WIDGET_STATES.FULL]: { width: '400px', height: '70vh' },
  },
};

/**
 * Detect device type based on screen width
 */
export const getDeviceType = () => {
  const width = window.innerWidth;
  if (width <= BREAKPOINTS.MOBILE) return DEVICE_TYPES.MOBILE;
  if (width <= BREAKPOINTS.TABLET) return DEVICE_TYPES.TABLET;
  return DEVICE_TYPES.DESKTOP;
};

/**
 * Check if browser supports dvh units
 */
export const supportsDvh = () => {
  try {
    return CSS.supports('height', '100dvh');
  } catch {
    return false;
  }
};

/**
 * Get widget dimensions for current state and device
 */
export const getWidgetDimensions = (state, deviceType = null) => {
  const device = deviceType || getDeviceType();
  const hasDvhSupport = supportsDvh();

  // Use fallback dimensions if dvh not supported
  if (!hasDvhSupport && WIDGET_DIMENSIONS_FALLBACK[device]?.[state]) {
    return WIDGET_DIMENSIONS_FALLBACK[device][state];
  }

  return WIDGET_DIMENSIONS[device]?.[state] || WIDGET_DIMENSIONS[DEVICE_TYPES.DESKTOP][state];
};

/**
 * Get safe area insets for notched devices
 */
export const getSafeAreaInsets = () => {
  const computedStyle = getComputedStyle(document.documentElement);
  return {
    top: computedStyle.getPropertyValue('--sat') || '0px',
    right: computedStyle.getPropertyValue('--sar') || '0px',
    bottom: computedStyle.getPropertyValue('--sab') || '0px',
    left: computedStyle.getPropertyValue('--sal') || '0px',
  };
};

/**
 * Message types for parent-iframe communication
 */
export const MESSAGE_TYPES = {
  INIT: 'init',
  DIMENSIONS: 'dimensions',
  STATE_CHANGE: 'stateChange',
  ORG: 'org',
  INVALID_ORG: 'invalidOrg',
  SET_PARENT_STYLE: 'setParentStyle',
  RESET_PARENT_STYLE: 'resetParentStyle',
  FLOATING_WIDGET_STYLE: 'floatingWidgetStyle',
  RESET_FLOATING_WIDGET_STYLE: 'resetFloatingWidgetStyle',
  WINDOW_SIZE: 'windowSize',
};

/**
 * Get the open (expanded) width for the current device
 * Mobile/Tablet: full viewport width; Desktop: fixed 400px
 */
export const getResponsiveOpenWidth = () => {
  const device = getDeviceType();
  if (device === DEVICE_TYPES.DESKTOP) return '400px';
  return '100vw';
};

/**
 * Get the open (expanded) height for the current device
 * Mobile: full viewport height; Tablet: 85vh; Desktop: 70vh
 */
export const getResponsiveOpenHeight = () => {
  const device = getDeviceType();
  if (device === DEVICE_TYPES.MOBILE) {
    return supportsDvh() ? '100dvh' : '100vh';
  }
  if (device === DEVICE_TYPES.TABLET) return '85vh';
  return '70vh';
};

export default {
  WIDGET_STATES,
  DEVICE_TYPES,
  BREAKPOINTS,
  WIDGET_DIMENSIONS,
  getDeviceType,
  getWidgetDimensions,
  getResponsiveOpenWidth,
  getResponsiveOpenHeight,
  supportsDvh,
  MESSAGE_TYPES,
};
